import { Card, Col, Spinner } from 'react-bootstrap';
import SubtleBadge from '@/components/common/SubtleBadge';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import { toPersianDigit } from '@/helpers/utils';
import { OverviewCardTitle } from './OverviewSectionTitle';

type AiSignalsPanelProps = {
  humanSignals: string[];
  aiSignals: string[];
  isPending?: boolean;
};

const SignalList = ({
  items,
  tone
}: {
  items: string[];
  tone: 'success' | 'warning';
}) => {
  const fillHeight = items.length >= 5;

  return (
    <div className="d-flex flex-column gap-2 flex-grow-1">
      {items.length === 0 ? (
        <div className="fs--1 text-500">موردی ثبت نشده است.</div>
      ) : (
        items.map((item, index) => (
          <div
            key={item}
            className={`d-flex align-items-center gap-3 bg-100 rounded-3 px-4 py-3 border position-relative overflow-hidden${
              fillHeight ? ' flex-grow-1' : ''
            }`}
          >
            <span
              className={`position-absolute top-0 bottom-0 bg-${tone}`}
              style={{ width: 3, insetInlineStart: 0 }}
            />
            <span className={`fw-bold text-${tone} fs--1 lh-1 flex-shrink-0`}>
              {toPersianDigit(String(index + 1).padStart(2, '0'))}
            </span>
            <span className="fs--1 text-700">{item}</span>
          </div>
        ))
      )}
    </div>
  );
};

const SignalListPending = ({ tone }: { tone: 'success' | 'warning' }) => (
  <div className="d-flex flex-column gap-2 flex-grow-1 ai-detection-pending-body">
    {[72, 88, 64, 80].map((width, index) => (
      <div
        key={index}
        className="d-flex align-items-center gap-3 bg-100 rounded-3 px-4 py-3 border position-relative overflow-hidden"
      >
        <span
          className={`position-absolute top-0 bottom-0 bg-${tone} opacity-50`}
          style={{ width: 3, insetInlineStart: 0 }}
        />
        <span className={`fw-bold text-${tone} fs--1 lh-1 flex-shrink-0 opacity-50`}>
          {toPersianDigit(String(index + 1).padStart(2, '0'))}
        </span>
        <div className="flex-grow-1">
          <TextLoading width={width} height={10} />
        </div>
      </div>
    ))}
    <div className="ai-detection-pending-hint d-flex align-items-center gap-2 mt-1 px-1">
      <Spinner
        animation="border"
        className="flex-shrink-0"
        style={{ width: 12, height: 12, borderWidth: 1.5 }}
      />
      <span className="fs--2 text-500">در حال استخراج نشانه‌ها از محتوا...</span>
    </div>
  </div>
);

const DetectionPendingBadge = () => (
  <SubtleBadge bg="info" pill className="d-inline-flex align-items-center gap-1">
    <Spinner
      animation="border"
      className="flex-shrink-0"
      style={{ width: 10, height: 10, borderWidth: 1.5 }}
    />
    در حال دریافت
  </SubtleBadge>
);

const AiSignalsPanel = ({
  humanSignals,
  aiSignals,
  isPending = false
}: AiSignalsPanelProps) => (
  <>
    <Col xs={12} md={6} xl={4}>
      <Card className="h-100">
        <FalconCardHeader
          title={
            <OverviewCardTitle
              icon="user"
              title="نشانه‌های انسانی"
              helpHtml="ویژگی‌ها و الگوهایی در متن که شباهت آن را به نوشتار انسانی بیشتر می‌کنند."
            />
          }
          titleTag="h6"
          className="py-2"
          light
          endEl={
            isPending ? (
              <DetectionPendingBadge />
            ) : (
              <SubtleBadge bg="success">
                {toPersianDigit(`${humanSignals.length}`)}
              </SubtleBadge>
            )
          }
        />
        <Card.Body className="d-flex flex-column">
          {isPending ? (
            <SignalListPending tone="success" />
          ) : (
            <SignalList items={humanSignals} tone="success" />
          )}
        </Card.Body>
      </Card>
    </Col>
    <Col xs={12} md={6} xl={4}>
      <Card className="h-100">
        <FalconCardHeader
          title={
            <OverviewCardTitle
              icon="robot"
              title="نشانه‌های AI"
              helpHtml="ویژگی‌ها و الگوهایی در متن که شباهت آن را به نوشتار ماشینی بیشتر می‌کنند."
            />
          }
          titleTag="h6"
          className="py-2"
          light
          endEl={
            isPending ? (
              <DetectionPendingBadge />
            ) : (
              <SubtleBadge bg="warning">
                {toPersianDigit(`${aiSignals.length}`)}
              </SubtleBadge>
            )
          }
        />
        <Card.Body className="d-flex flex-column">
          {isPending ? (
            <SignalListPending tone="warning" />
          ) : (
            <SignalList items={aiSignals} tone="warning" />
          )}
        </Card.Body>
      </Card>
    </Col>
  </>
);

export default AiSignalsPanel;
